/**
 * #987 — the Polymarket US price pills on a main-board team row.
 *
 * Prices come from the US catalogue's best bid/ask for the game's single
 * moneyline. A board "team — YES/NO" pill maps to the exact US outcome
 * through `usOutcomeFor` (the producer's named YES team), never by team order.
 * Maker shows the best bid, taker the best ask; there is no US last-trade
 * price in this source, so market mode shows no price rather than a guess.
 */
import { usEvidenceNow } from '@/lib/polymarketUs/timeEvidence';
import { createContext, useContext } from 'react';
import type { MarketSide, PriceMode, TeamSide } from '../types';
import type { UsBoardQuote } from '@/stores/usBoardQuotes';
import { usdToCents, usOutcomeFor } from '@/lib/polymarketUs/gameMapping';
import { PricePill } from '../ladder/PricePill';
import { Td } from '@/components/atoms/TableCells';

/**
 * #1114 (T8 row 7): why a US board pill can't trade, for PricePill's title
 * ("Not tradable now: <reason>"). Passed only while the pill is disabled.
 */
const US_NO_QUOTE_PILL_REASON = 'no confirmed US quote (feed offline, withdrawn or not yet received)';

/**
 * #1207 — Game Detail's Moneyline holds both pills at their full width (price
 * and odds), so a live tick or a quote going stale never slides them. A
 * context, so the shared row renderer (GameRows) needs no new prop; the board
 * keeps its current pills.
 */
export const UsCellHoldWidthContext = createContext(false);

/** Cents a US pill shows for one team and board side, or null. */
export function usPillCents(
  market: UsBoardQuote, teamCode: string, boardSide: MarketSide, priceMode: PriceMode, now: number,
): number | null {
  if (!usQuoteCurrent(market, now) || !market.yesTeam || priceMode === 'market') return null;
  const outcome = usOutcomeFor(teamCode, market.yesTeam, boardSide);
  const b = market.book;
  const usd = priceMode === 'maker'
    ? (outcome === 'yes' ? b.yesBidUsd : b.noBidUsd)
    : (outcome === 'yes' ? b.yesAskUsd : b.noAskUsd);
  return usd === null ? null : usdToCents(usd);
}

/** #1232 — the board's Hide-empty rule: a current US quote with any price
 * keeps a row from being "empty", as a K/P book with a price does. Read at
 * the moment asked, like the board quote's own `currentAt()`. */
export function usQuoteHasCurrentPrice(market: UsBoardQuote): boolean {
  const b = market.book;
  return usQuoteCurrent(market, Date.now())
    && [b.yesBidUsd, b.yesAskUsd, b.noBidUsd, b.noAskUsd].some((usd) => usd !== null);
}

export function usQuoteCurrent(market: UsBoardQuote, now: number): boolean {
  if ('currentAt' in market) return market.currentAt();
  now = usEvidenceNow(market.timeEvidence, now);
  return market.book.state === 'current' && market.book.expiresAtMs !== null
    && market.book.expiresAtMs > now && market.classification.expiresAtMs > now;
}

export interface UsBoardCellProps {
  market: UsBoardQuote | null;
  plan?: 'current' | 'pending' | null;
  now: number;
  teamCode: string;
  teamSide: TeamSide;
  priceMode: PriceMode;
  activeMarketSide: MarketSide;
  sideTint: boolean;
  isFinal: boolean;
  onClick(side: TeamSide, boardSide: MarketSide, priceCents: number | null): void;
}

export function UsBoardCell(props: UsBoardCellProps) {
  return <Td className="whitespace-nowrap text-center"><UsBoardPills {...props} /></Td>;
}

/** Same native Y/N quote controls in table cells and compact venue groups. */
export function UsBoardPills({
  market, plan = 'current', now, teamCode, teamSide, priceMode, activeMarketSide, sideTint, isFinal, onClick,
}: UsBoardCellProps) {
  const holdWidth = useContext(UsCellHoldWidthContext);
  if (isFinal || plan === null) {
    return (
      <span className="text-muted-foreground/40" title={market ? undefined : 'No Polymarket US market for this game'}>—</span>
    );
  }
  const yes = market ? usPillCents(market, teamCode, 'yes', priceMode, now) : null;
  const no = market ? usPillCents(market, teamCode, 'no', priceMode, now) : null;
  const unavailable = !market || !usQuoteCurrent(market, now);
  const reason = plan === 'pending' ? 'Integration pending'
    : unavailable ? 'No current US quote' : priceMode === 'market' ? 'No last-trade price' : '\u00a0';
  return (
    <div className="whitespace-nowrap text-center">
      <div className="relative flex w-80 items-center justify-center gap-1.5" data-testid={`us-cell-${teamSide}`}>
        <PricePill
          label="Y"
          cents={yes}
          side="yes"
          sideTint={sideTint}
          activeMarketSide={activeMarketSide}
          priceMode={priceMode}
          disabled={unavailable}
          disabledReason={unavailable ? US_NO_QUOTE_PILL_REASON : undefined}
          holdPriceWidth={holdWidth}
          holdOddsWidth={holdWidth}
          testId={`pill-polymarket_us-${teamSide}-Y`}
          onClick={(e) => {
            e.stopPropagation();
            if (market && usQuoteCurrent(market, Date.now())) onClick(teamSide, 'yes', usPillCents(market, teamCode, 'yes', priceMode, Date.now()));
          }}
        />
        <PricePill
          label="N"
          cents={no}
          side="no"
          sideTint={sideTint}
          activeMarketSide={activeMarketSide}
          priceMode={priceMode}
          disabled={unavailable}
          disabledReason={unavailable ? US_NO_QUOTE_PILL_REASON : undefined}
          holdPriceWidth={holdWidth}
          holdOddsWidth={holdWidth}
          testId={`pill-polymarket_us-${teamSide}-N`}
          onClick={(e) => {
            e.stopPropagation();
            if (market && usQuoteCurrent(market, Date.now())) onClick(teamSide, 'no', usPillCents(market, teamCode, 'no', priceMode, Date.now()));
          }}
        />
      </div>
      <div className="h-3 text-[10px] leading-3 text-muted-foreground" data-testid={`us-quote-status-${teamSide}`}>
        {reason}
      </div>
    </div>
  );
}
